import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { DateFormats, Timezones } from 'infrastructure/enums';
import { useState } from 'react';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';

import { getNursingInterventionValues } from '../helpers';
import {
  hypertensionSymptoms,
  hypotensionSymptoms,
} from '../templates/blood-pressure';
import { generatePressureMessage } from '../blurbs/blood-pressure';

import type { IUseBloodPressureProps } from '../types';
import type { IBloodPressureValues } from 'components/molecules/rpm-alert-assessment/types';

export const useBloodPressureAssessment = ({
  type,
  activityId,
  activityDate,
  values,
  patientGuid,
  addComment,
  closeModal,
  handleSubmit,
}: IUseBloodPressureProps) => {
  const [openEmergencyModal, setOpenEmergencyModal] = useState<boolean>(false);

  const initialValues: IBloodPressureValues = {
    // Q1
    patient: undefined,
    other: null,
    relationship: null,
    // Q2
    AOC: null,
    // Q3
    last: null,
    next: null,
    // Q4
    isOneMoreReading: null,
    repeatReadingWas: null,
    bounds: null,
    // Q5
    symptoms: null,
    confirmedSymptoms: [],
    deniedSymptoms: [],
    symptomsOnset: null,
    emergencyCare: null,
    checkSymptoms: true,
    // Q6
    medicationTiming: null,
    // Q7
    isTakeMedications: null,
    medications: null,
    // Q8
    isTakePrnMedications: null,
    // Q9
    confirmedFactors: [],
    deniedFactors: [],
    // Q10
    details: null,
    // Q11
    nursingIntervention: [],
    notEscalated: [],
    interventionDetails: null,
    escalationToClinic: [],
    // Q12
    verbalized: null,
  };

  const { patientInfo } = usePatientInfo({
    patientGuid,
  });

  const symptomsList =
    type === 'min' ? hypotensionSymptoms : hypertensionSymptoms;

  const emergentSymptoms = symptomsList
    .filter((item) => item.required)
    .map((item) => item.label);

  const preparedData = (data: IBloodPressureValues) => {
    const reading = `${values.systolic}/${values.diastolic} mmHg`;
    const timeZone = patientInfo?.loginInfo?.timezone || 'America/Chicago';

    const dataInPatientTimeZone = convertDateToTimeZone(
      new Date(Number(activityDate)),
      Timezones[timeZone],
    );
    const dateOfReading = DateFormatter({
      date: dataInPatientTimeZone,
      format: DateFormats['MM-dd-yyyy'],
    });

    const lastDate = data.last
      ? DateFormatter({
          date: new Date(data.last),
          format: DateFormats['MM-dd-yyyy'],
        })
      : null;

    const nextDate = data.next
      ? DateFormatter({
          date: new Date(data.next),
          format: DateFormats['MM-dd-yyyy'],
        })
      : null;

    const nursingInterventionValues = getNursingInterventionValues(
      data?.nursingIntervention,
    );
    const confirmedEmergentSymptoms = data.confirmedSymptoms?.filter(
      (item: string) => emergentSymptoms.includes(item),
    );
    const confirmedSymptomsWithoutEmergent = data.confirmedSymptoms?.filter(
      (item: string) => !emergentSymptoms.includes(item),
    );

    const isSpeakWithPatient = data.patient === 'Patient';

    return {
      Q1: {
        reading,
        dateOfReading,
        patient: data.patient,
        relationship: data.relationship,
        speakWithPatient: isSpeakWithPatient,
        speakWithOther: data.other,
      },
      Q2: {
        AOC: data.AOC,
      },
      Q3: {
        last: lastDate,
        next: nextDate,
      },
      Q4: {
        isOneMoreReading: data.isOneMoreReading ?? null,
        bounds: data.bounds ?? null,
        repeatReadingWas: data.repeatReadingWas ?? null,
      },
      Q5: {
        confirmedSymptoms: confirmedSymptomsWithoutEmergent,
        deniedSymptoms: data.deniedSymptoms,
        isAllSymptomsDenied:
          data.deniedSymptoms?.length === symptomsList.length,
        confirmedEmergentSymptoms,
        symptomsOnset: data.symptomsOnset,
        emergencyModalAnswer: data.emergencyCare,
      },
      Q6: {
        medicationTiming: data.medicationTiming,
      },
      Q7: {
        isTakeMedications: data.isTakeMedications,
        medications: data.medications,
      },
      Q8: {
        isTakePrnMedications: data.isTakePrnMedications,
      },
      Q9: {
        details: data.details,
      },
      Q10: {
        confirmedFactors: data.confirmedFactors,
        deniedFactors: data.deniedFactors,
      },
      Q11: {
        ...nursingInterventionValues,
        notEscalated: data.notEscalated,
        interventionDetails: data.interventionDetails,
        escalationToClinic: data.escalationToClinic,
      },
      Q12: {
        verbalized: data.verbalized,
      },
    };
  };

  const toggleEmergencyModal = () => setOpenEmergencyModal((prev) => !prev);

  const onSaveForm = (formData: IBloodPressureValues) => {
    const preparedValues = preparedData(formData);
    const message = generatePressureMessage(preparedValues);

    handleSubmit(message);
    addComment(activityId);
    closeModal();
  };

  return {
    initialValues,
    openEmergencyModal,
    symptomsList,
    onSaveForm,
    toggleEmergencyModal,
  };
};
